import { IStringTool } from "../core/IStringTool"

/**
 * Input for removing transform effects from an element's bounding box.
 */
interface TransformNullifyInput {
  /** The DOM element whose CSS transform should be nullified. */
  element: HTMLElement
}

/**
 * Output with corrected bounding box values.
 */
interface TransformNullifyOutput {
  /** Top position without transform effects. */
  top: number

  /** Left position without transform effects. */
  left: number

  /** Width without transform scaling. */
  width: number

  /** Height without transform scaling. */
  height: number
}

/**
 * Computes the true bounding box of a DOM element,
 * nullifying CSS `transform: matrix(...)` effects.
 */
export default class TransformNullifyTool
  implements IStringTool<TransformNullifyInput, TransformNullifyOutput>
{
  /**
   * @returns Element position and size without transform influence.
   */
  process({ element }: TransformNullifyInput): TransformNullifyOutput {
    const rect = element.getBoundingClientRect()
    const matrix = getComputedStyle(element).transform

    const values = matrix
      .match(/-?[\d.]+/g)
      ?.map(parseFloat) ?? []

    if (values.length === 6) {
      const [a, b, c, d, e, f] = values
      const det = a * d - b * c

      return {
        width: rect.width / (a || 1),
        height: rect.height / (d || 1),
        left: (rect.left * d - rect.top * c + c * f - e * d) / det,
        top: (-rect.left * b + rect.top * a + e * b - a * f) / det,
      }
    }

    return rect
  }
}
